import React from 'react';
import { useRecategorizeQueueRequestMutation } from 'hooks/queue';
import { AskRequestStatus } from '@hone-automation/common';
import type { ReCategorizeButtonProps } from './RecategorizeButton';
import { useQueueContext } from '../../Queue/QueueContext';
import { useEvent } from '../../../../hooks/useEvent';

// Define the expected props for the HOC
export type WithAskRequestsProps = {
  txId: string;
  locationId: string;
  activeTab: AskRequestStatus;
};

const withAskRequests = <P extends WithAskRequestsProps>(
  WrappedComponent: ({ onSubmit, isPending }: ReCategorizeButtonProps) => React.JSX.Element
) => {
  return function WithAskRequests(props: P) {
    const { locationId, activeTab } = props;

    const { dispatch } = useQueueContext();
    const { dispatch: emitEvent } = useEvent('onQueueRequestResolve');

    const reCategorizeTransaction = useRecategorizeQueueRequestMutation(locationId!, activeTab, (_, trx) => {
      // If the status has changed, provide message about viewing in different tab
      const statusChanged = trx.status !== activeTab;

      dispatch({
        type: 'UPDATE_REQUEST',
        payload: {
          request: trx,
          status: trx.status,
        },
      });

      dispatch({ type: 'SET_SELECTED_ITEM', payload: trx });

      emitEvent({
        field: trx.status,
        duration: 5000,
        message: statusChanged
          ? `Request status updated to ${trx.status}. Go to ${trx.status} tab to view it.`
          : undefined,
      });
    });

    const recategorize = async ({ comment, account }: { comment: string; account: string }) => {
      reCategorizeTransaction.mutate({ trxId: props.txId, newAccountId: account, comment });
    };

    // Extract only the necessary props for the wrapped component
    const wrappedComponentProps = {
      isPending: reCategorizeTransaction.isPending,
      onSubmit: recategorize,
    } as ReCategorizeButtonProps;

    return <WrappedComponent {...wrappedComponentProps} />;
  };
};

export default withAskRequests;
